import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { customAPIRequest } from '../auth/util/util';
import { loaderActions } from '../actions/loaderActions';
import { mysphereAPIURL } from '../auth/util/CustomConstants';
import { getAccessToken } from '../../services/helpers';
import Loader from './Loader';
import Header from './Header';
import './RegisterCodespace.css';

const allowedDomains = [
    { suffix: '.app.github.dev', env: 'codespaces' },
    { suffix: '.cloudshell.dev', env: 'cloudshell' },
    { suffix: '.gradio.live', env: 'gradiolive' },
    { suffix: '.ngrok-free.app', env: 'ngrok' },
];

function isValidCodespaceUrl(value) {
    if (!value || !value.startsWith('https://')) return false;
    try {
        const { hostname } = new URL(value);
        return allowedDomains.some(({ suffix }) => hostname.endsWith(suffix));
    } catch {
        return false;
    }
}

function RegisterCodespace() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [notEnrolled, setNotEnrolled] = useState(false);
    const [gradioUrl, setGradioUrl] = useState('');
    const [langflowUrl, setLangflowUrl] = useState('');
    const [gradioError, setGradioError] = useState('');
    const [langflowError, setLangflowError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const courseId = searchParams.get('courseId');

    useEffect(() => {
        dispatch(loaderActions.start());
        setLoading(true);
        const init = async () => {
            try {
                if (
                    localStorage.getItem('userDetailsID') === '' ||
                    !localStorage.getItem('userDetailsID')
                ) {
                    navigate(process.env.PUBLIC_URL + '/my-feed');
                    return;
                }

                const userEmail = localStorage.getItem('email');
                const userResponse = await customAPIRequest('core_user_get_users_by_field', {
                    'field': 'email',
                    'values[0]': userEmail,
                });
                const userId = userResponse?.data?.[0]?.id;

                if (!userId) {
                    setNotEnrolled(true);
                    return;
                }

                const coursesResponse = await customAPIRequest('core_enrol_get_users_courses', {
                    'userid': userId,
                    'returnusercount': 0,
                });
                const enrolledCourses = coursesResponse?.data || [];
                const isEnrolled = enrolledCourses.some(
                    (course) => String(course.id) === String(courseId)
                );

                if (!isEnrolled) {
                    setNotEnrolled(true);
                }
            } catch (error) {
                console.error('Error in RegisterCodespace init', error);
            } finally {
                dispatch(loaderActions.stop());
                setLoading(false);
            }
        };
        init();
    }, []);

    const validateGradio = () => {
        if (!isValidCodespaceUrl(gradioUrl)) {
            setGradioError(
                'Please enter a valid https URL ending with .gradio.live, .app.github.dev, .cloudshell.dev, or .ngrok-free.app.'
            );
            return false;
        }
        setGradioError('');
        return true;
    };

    const validateLangflow = () => {
        if (!isValidCodespaceUrl(langflowUrl)) {
            setLangflowError(
                'Please enter a valid https URL ending with .app.github.dev, .gradio.live, .cloudshell.dev, or .ngrok-free.app.'
            );
            return false;
        }
        setLangflowError('');
        return true;
    };

    const isFormValid = isValidCodespaceUrl(gradioUrl) && isValidCodespaceUrl(langflowUrl);

    const handleSubmit = async () => {
        const gradioOk = validateGradio();
        const langflowOk = validateLangflow();
        if (!gradioOk || !langflowOk) return;

        try {
            setSubmitting(true);
            setSubmitError('');
            const accessToken = await getAccessToken();
            await axios.post(
                `${mysphereAPIURL}/api/v1/proxy/register-codespace`,
                {
                    moodleCourseId: courseId,
                    gradioLiveUrl: gradioUrl,
                    langflowLiveUrl: langflowUrl,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            setSubmitted(true);
        } catch (error) {
            const message =
                error?.response?.data?.message || 'Registration failed. Please try again.';
            setSubmitError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRegisterAgain = () => {
        setGradioUrl('');
        setLangflowUrl('');
        setGradioError('');
        setLangflowError('');
        setSubmitError('');
        setSubmitted(false);
    };

    return loading ? (
        <Loader />
    ) : (
        <>
            <Header />
            <div className='register-codespace-page'>
                {notEnrolled ? (
                    <div className='register-codespace-card'>
                        <p className='register-codespace-warning'>
                            You are not allowed to access this page.
                        </p>
                        <Button
                            variant='contained'
                            onClick={() => navigate(process.env.PUBLIC_URL + '/my-feed')}
                            style={{ backgroundColor: '#58A7A6' }}
                        >
                            Go to My Feed
                        </Button>
                    </div>
                ) : submitted ? (
                    <div className='register-codespace-card'>
                        <p className='register-codespace-success'>
                            Your Codespace URL has been whitelisted successfully.
                        </p>
                        <Button
                            variant='contained'
                            onClick={handleRegisterAgain}
                            style={{ backgroundColor: '#58A7A6' }}
                        >
                            Register Again
                        </Button>
                    </div>
                ) : (
                    <div className='register-codespace-card'>
                        <h2>Register Codespace</h2>
                        <div className='register-codespace-field'>
                            <label>Gradio Live URL</label>
                            <input
                                type='text'
                                value={gradioUrl}
                                placeholder='e.g., https://9ca9f33be4727ecc24.gradio.live'
                                onChange={(e) => setGradioUrl(e.target.value)}
                                onBlur={validateGradio}
                            />
                            {gradioError && (
                                <span className='register-codespace-error'>{gradioError}</span>
                            )}
                        </div>
                        <div className='register-codespace-field'>
                            <label>Langflow Live URL</label>
                            <input
                                type='text'
                                value={langflowUrl}
                                placeholder='e.g., https://abc123.app.github.dev'
                                onChange={(e) => setLangflowUrl(e.target.value)}
                                onBlur={validateLangflow}
                            />
                            {langflowError && (
                                <span className='register-codespace-error'>{langflowError}</span>
                            )}
                        </div>
                        <Button
                            variant='contained'
                            disabled={!isFormValid || submitting}
                            onClick={handleSubmit}
                            style={{
                                backgroundColor: isFormValid && !submitting ? '#58A7A6' : undefined,
                                marginTop: '16px',
                            }}
                            startIcon={
                                submitting ? <CircularProgress size={16} color='inherit' /> : null
                            }
                        >
                            {submitting ? 'Registering...' : 'Register Codespace'}
                        </Button>
                        {submitError && (
                            <span className='register-codespace-submit-error'>{submitError}</span>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default RegisterCodespace;
